import { useEffect, useRef, useState } from 'react';

const POLL_MS = 2000;
const REQUEST_MS = 15000;
const WATCH_MS = 180000;
const MAX_ELIGIBILITY_WAIT_MS = 3600000;

// One activity-scoped chain: discovery precedes generation. Polls only read
// canonical state; neither transport recovery nor exhaustion spends an attempt.
export default function useWorkoutRecommendationJob({ activityId, backendService, enabled, autoGenerate, version, onRecommendations }) {
  const onRecommendationsRef = useRef(onRecommendations);
  onRecommendationsRef.current = onRecommendations;
  const commands = useRef({});
  const [state, setState] = useState({ status: 'idle' });
  useEffect(() => {
    let cancelled = false;
    let timer;
    let deadline;
    let activeController;
    let cancelRequest;
    let epoch = 0;
    let busy = false;
    let reconciliationRequested = false;
    let latestJob;
    let mayStart = false;
    let watchUntil = 0;
    let readFailures = 0;
    let needsRecovery = false;
    let denied = false;
    let lastResume = -Infinity;
    let eligibilityIntent = null;
    const foreground = () => document.visibilityState !== 'hidden' && navigator.onLine !== false;
    const eligibilityReads = new Set();
    const current = token => !cancelled && token === epoch;
    const stopTimers = () => { clearTimeout(timer); clearTimeout(deadline); deadline = null; };
    const beginWatch = (explicit = false) => {
      // Only an explicit user operation or advertised eligibility check owns a
      // new finite watch. Passive failure/resume never renews this budget.
      if (explicit) { stopTimers(); watchUntil = 0; readFailures = 0; }
      if (deadline || !foreground()) return;
      if (!watchUntil) watchUntil = Date.now() + WATCH_MS;
      deadline = setTimeout(() => {
        needsRecovery = true;
        epoch += 1;
        stopTimers();
        activeController?.abort();
        cancelRequest?.();
        busy = false;
        setState({ status: 'paused' });
      }, Math.max(0, watchUntil - Date.now()));
    };
    // Race the entire service promise, including authentication before axios
    // dispatch. Aborting alone does not settle a stuck auth interceptor.
    async function request(call) {
      const controller = new AbortController();
      activeController = controller;
      let timeout;
      try {
        return await Promise.race([
          Promise.resolve().then(() => {
            if (controller.signal.aborted) throw new Error('cancelled');
            return call(controller.signal);
          }),
          new Promise((_, reject) => {
            cancelRequest = () => reject(new Error('cancelled'));
            timeout = setTimeout(() => { controller.abort(); reject(new Error('deadline')); }, REQUEST_MS);
          }),
        ]);
      } finally {
        clearTimeout(timeout);
        if (activeController === controller) { activeController = null; cancelRequest = null; }
      }
    }
    setState({ status: 'idle' });
    commands.current = {};
    if (!enabled) return;

    const resumeEligibility = () => {
      if (!eligibilityIntent || !foreground()) return;
      clearTimeout(timer);
      if (eligibilityIntent.retryAt > Date.now()) {
        timer = setTimeout(resumeEligibility, eligibilityIntent.retryAt - Date.now());
        return;
      }
      eligibilityIntent = null;
      if (busy) { reconciliationRequested = true; return; }
      beginWatch(true);
      read(false);
    };

    const apply = (result, token, recoverEmpty = false) => {
      if (!current(token)) return;
      clearTimeout(timer);
      if (result?.recommendations) onRecommendationsRef.current(result.recommendations);
      latestJob = result?.job;
      mayStart = false;
      const status = latestJob?.status;
      if (status === 'pending' || status === 'running') {
        needsRecovery = true;
        if (watchUntil && Date.now() >= watchUntil) { stopTimers(); setState({ status: 'paused' }); return; }
        beginWatch();
        setState({ status: 'pending' });
        if (foreground()) timer = setTimeout(() => read(false, token), POLL_MS);
      } else {
        needsRecovery = false;
        const previousEligibility = eligibilityIntent;
        eligibilityIntent = null;
        stopTimers();
        if (status === 'failed') {
          const retryAt = Date.parse(latestJob.retry_after);
          const waiting = Number.isFinite(retryAt) && retryAt > Date.now();
          setState({ status: 'failed', job: latestJob, waiting, canRetry: autoGenerate && latestJob.retryable === true && !waiting });
          // At most one canonical eligibility check per advertised date. Past
          // or absent dates require manual refresh, never endless failed polling.
          const key = `${latestJob.id}:${latestJob.retry_after}`;
          if (waiting && retryAt - Date.now() <= MAX_ELIGIBILITY_WAIT_MS
            && (!eligibilityReads.has(key) || previousEligibility?.key === key)) {
            eligibilityReads.add(key);
            eligibilityIntent = { key, retryAt };
            resumeEligibility();
          }
        } else if (recoverEmpty && autoGenerate && (!result?.recommendations || result?.stale)) {
          mayStart = true;
          setState({ status: 'not-started', canRetry: true });
        } else if (status === 'succeeded') {
          setState({ status: 'succeeded' });
        } else if (status === 'superseded') {
          setState({ status: 'superseded' });
        } else setState({ status: 'idle' });
      }
    };
    async function read(initial = false, token = epoch, recoverEmpty = false) {
      if (!current(token) || busy) return;
      if (!foreground()) { needsRecovery = true; return; }
      clearTimeout(timer);
      busy = true;
      let shouldStart = false;
      try {
        const result = await request(signal => backendService.getStoredWorkoutRecommendations(activityId, signal));
        if (!current(token)) return;
        apply(result, token, recoverEmpty);
        shouldStart = initial && autoGenerate && !['pending', 'running', 'failed', 'superseded'].includes(result?.job?.status)
          && (result?.stale || !result?.recommendations || Object.keys(result.recommendations).length === 0);
      } catch (error) {
        if (current(token)) {
          clearTimeout(timer);
          denied = [401, 403].includes(error?.response?.status ?? error?.status);
          needsRecovery = !denied;
          if (denied) eligibilityIntent = null;
          setState({ status: 'unavailable' });
          const delay = POLL_MS * (2 ** readFailures++);
          if (!denied && readFailures <= 4 && Date.now() + delay < watchUntil && foreground()) {
            timer = setTimeout(() => read(false, token), delay);
          } else stopTimers();
        }
      } finally { if (current(token)) busy = false; }
      if (reconciliationRequested && current(token)) {
        reconciliationRequested = false;
        return read(false, token, false);
      }
      if (shouldStart && current(token)) await start(false, token);
    }
    async function start(force, token = epoch) {
      if (!current(token) || busy || !autoGenerate) return;
      busy = true;
      clearTimeout(timer);
      beginWatch();
      setState({ status: 'pending' });
      let reconcile = false;
      try {
        const result = await request(signal => backendService.getWorkoutExerciseRecommendations(activityId, force, signal));
        if (current(token)) {
          // Even a successful enqueue is not proof of persisted recommendations.
          // Pending payloads retain old advice until canonical GET settles them.
          if (result) apply(result, token, true);
          else reconcile = true;
        }
      } catch (_) { reconcile = current(token); }
      finally { if (current(token)) busy = false; }
      if (reconciliationRequested && current(token)) {
        reconciliationRequested = false;
        await read(false, token, false);
      } else if (reconcile && current(token)) await read(false, token, true);
    }
    commands.current = {
      refresh: () => {
        if (!current(epoch) || busy) return;
        clearTimeout(timer);
        beginWatch(true);
        setState({ status: 'checking' });
        return read(false, epoch, true);
      },
      retry: () => {
        if (!mayStart && (latestJob?.status !== 'failed' || latestJob.retryable !== true || Date.parse(latestJob.retry_after) > Date.now())) return;
        if (busy) return;
        beginWatch(true);
        return start(!mayStart);
      },
      // A successful per-exercise Coach request may have canonically
      // superseded a terminal legacy batch failure. Reconcile once, read-only;
      // never feed this path into generation eligibility.
      reconcile: result => {
        if (!current(epoch)) return;
        if (busy) { reconciliationRequested = true; return; }
        if (result) return apply(result, epoch, false);
        clearTimeout(timer);
        beginWatch();
        setState({ status: 'checking' });
        return read(false, epoch, false);
      },
    };
    const onResume = () => {
      if (!foreground()) {
        needsRecovery = needsRecovery || busy;
        epoch += 1;
        stopTimers();
        activeController?.abort();
        cancelRequest?.();
        busy = false;
        return;
      }
      if (eligibilityIntent) { resumeEligibility(); return; }
      if (!needsRecovery || denied || busy || Date.now() - lastResume < POLL_MS) return;
      lastResume = Date.now();
      // One catchup, never renew the original automatic watch budget.
      read(false);
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('online', onResume);
    window.addEventListener('offline', onResume);
    beginWatch();
    read(true);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('online', onResume);
      window.removeEventListener('offline', onResume);
      cancelled = true;
      stopTimers();
      activeController?.abort();
      cancelRequest?.();
    };
  }, [activityId, backendService, enabled, autoGenerate, version]);
  return { ...state, isLoading: enabled && state.status === 'pending', retry: () => commands.current.retry?.(), refresh: () => commands.current.refresh?.(), reconcile: result => commands.current.reconcile?.(result) };
}
